import { audioContext, isMuted } from '../audio';
import { readJson, writeJson } from '../../core/storage/local';
import { Aux } from './aux';
import { loadLiveStations, type LiveStation } from './liveStations';
import { Sequencer } from './sequencer';
import { createSpeaker } from './speaker';
import { SYNTH_STATIONS, type SynthStation } from './stations';

export type Channel = { kind: 'synth'; station: SynthStation } | { kind: 'live'; station: LiveStation } | { kind: 'off' };

export interface NowPlaying {
  badge: 'MIX' | 'FM' | 'AUX' | 'OFF' | '···';
  name: string;
  detail: string;
  paused: boolean;
}

/** One row of the station list. */
export interface StationRow {
  index: number;
  badge: 'MIX' | 'FM';
  name: string;
  detail: string;
  current: boolean;
}

const PICK_KEY = 'trotrorun.radio.v1';
const NO_SIGNAL_MS = 9000;

/** The dashboard radio: built-in stations, live Ghana FM, AUX, or off. Skip buttons cycle, the list picks one, pause/play holds it. */
export class Radio {
  private channels: Channel[] = [...SYNTH_STATIONS.map((station) => ({ kind: 'synth' as const, station })), { kind: 'off' }];
  private index = 0;
  private sequencer: Sequencer | null = null;
  private speaker: GainNode | null = null;
  /** A fresh element per live station, so a stopped stream's late error can't knock out the next one. */
  private live: HTMLAudioElement | null = null;
  private muted = false;
  private liveTimer = 0;
  private liveLoading: Promise<void> | null = null;
  private paused = false;
  private readonly aux = new Aux();
  private auxOn = false;
  private now: NowPlaying = { badge: 'OFF', name: 'Radio', detail: 'Tap here to pick a station', paused: false };
  onChange: (now: NowPlaying) => void = () => undefined;

  constructor() {
    const saved = readJson<{ id: string; live?: LiveStation }>(PICK_KEY);
    // A live station isn't in the built-in list yet: keep the saved copy so it can play straight away.
    if (saved?.live) this.channels.splice(this.channels.length - 1, 0, { kind: 'live', station: saved.live });
    const found = this.channels.findIndex((c) => this.idOf(c) === saved?.id);
    this.index = found >= 0 ? found : 0;
    this.aux.onTrack = (name) => this.show({ badge: 'AUX', name, detail: 'Your music' });
  }

  get nowPlaying(): NowPlaying {
    return this.now;
  }

  /** Start playing the saved channel (call after a user gesture). */
  turnOn(): void {
    if (this.now.badge === 'OFF' && !this.paused && this.channels[this.index]?.kind !== 'off') this.tune();
  }

  get isPaused(): boolean {
    return this.paused;
  }

  /** Stops the sound but keeps the station, so play carries on where it was tuned. */
  pause(): void {
    if (this.paused || this.now.badge === 'OFF' || this.now.badge === '···') return;
    this.paused = true;
    if (this.auxOn) this.aux.pause();
    else this.stopAll();
    this.show(this.now);
  }

  resume(): void {
    if (!this.paused) return;
    this.paused = false;
    if (this.auxOn) {
      this.aux.resume();
      this.show(this.now);
    } else this.tune();
  }

  togglePause(): void {
    if (this.paused) this.resume();
    else if (this.now.badge === 'OFF') this.turnOnFromOff();
    else this.pause();
  }

  /** The stations to choose from (loads the live ones the first time). */
  async stationList(): Promise<StationRow[]> {
    await this.ensureLiveStations(true);
    const rows: StationRow[] = [];
    this.channels.forEach((channel, index) => {
      if (channel.kind === 'off') return;
      const synth = channel.kind === 'synth';
      rows.push({
        index,
        badge: synth ? 'MIX' : 'FM',
        name: channel.station.name,
        detail: synth ? `Built-in · ${channel.station.genre}` : channel.station.detail,
        current: index === this.index && !this.auxOn && this.now.badge !== 'OFF',
      });
    });
    return rows;
  }

  /** Tune to a row from stationList(). Also un-pauses. */
  async tuneTo(index: number): Promise<void> {
    if (!this.channels[index]) return;
    if (this.auxOn) {
      this.auxOn = false;
      this.aux.stop();
    }
    this.index = index;
    this.tune();
  }

  /** ▶ on a radio that's off: play the saved station, or the first one. */
  private turnOnFromOff(): void {
    if (this.channels[this.index]?.kind === 'off') this.index = 0;
    this.tune();
  }

  next(): void {
    this.move(1);
  }

  prev(): void {
    this.move(-1);
  }

  async toggleAux(): Promise<void> {
    if (this.auxOn) {
      this.auxOn = false;
      this.aux.stop();
      return this.tune();
    }
    if (!this.aux.hasTracks && !(await this.aux.choose())) return;
    const ctx = audioContext();
    if (!ctx) return;
    this.stopAll();
    this.paused = false;
    this.auxOn = true;
    this.aux.connect(ctx, this.ensureSpeaker(ctx));
    this.aux.play();
  }

  /** Busier drums as you go faster (0–1). */
  setEnergy(energy: number): void {
    if (this.sequencer) this.sequencer.energy = energy;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.live) this.live.muted = muted;
  }

  private async move(delta: number): Promise<void> {
    if (this.auxOn) {
      if (delta > 0) this.aux.skip();
      return;
    }
    await this.ensureLiveStations(false);
    this.index = (this.index + delta + this.channels.length) % this.channels.length;
    this.tune();
  }

  /** Loads the live list once. `quiet` keeps the screen showing what's playing meanwhile. */
  private ensureLiveStations(quiet: boolean): Promise<void> {
    this.liveLoading ??= this.loadLive(quiet);
    return this.liveLoading;
  }

  private async loadLive(quiet: boolean): Promise<void> {
    if (!quiet) this.show({ badge: '···', name: 'Tuning…', detail: 'Finding stations' });
    const stations = await loadLiveStations();
    const currentId = this.idOf(this.channels[this.index] ?? { kind: 'off' });
    const synth = this.channels.filter((c) => c.kind === 'synth');
    // The station you were on stays available even if today's directory list no longer has it.
    const kept = this.channels.filter((c) => c.kind === 'live' && !stations.some((s) => s.id === c.station.id));
    const live = stations.map((station) => ({ kind: 'live' as const, station }));
    this.channels = [...synth, ...live, ...kept, { kind: 'off' }];
    const found = this.channels.findIndex((c) => this.idOf(c) === currentId);
    this.index = found >= 0 ? found : 0;
  }

  private tune(): void {
    this.stopAll();
    this.paused = false;
    const channel = this.channels[this.index];
    writeJson(PICK_KEY, { id: channel ? this.idOf(channel) : 'off', live: channel?.kind === 'live' ? channel.station : undefined });
    if (!channel || channel.kind === 'off') return this.show({ badge: 'OFF', name: 'Radio off', detail: 'Tap here to pick a station' });
    const ctx = audioContext();
    if (!ctx) return this.show({ badge: 'OFF', name: 'Muted', detail: 'Press M to unmute' });
    if (channel.kind === 'synth') {
      this.sequencer = new Sequencer(ctx, this.ensureSpeaker(ctx), channel.station.style);
      this.sequencer.start();
      return this.show({ badge: 'MIX', name: channel.station.name, detail: `Built-in · ${channel.station.genre}` });
    }
    const live = new Audio(channel.station.url);
    this.live = live;
    live.muted = this.muted || isMuted();
    const failed = (): void => {
      if (this.live === live) this.noSignal();
    };
    live.addEventListener('playing', () => window.clearTimeout(this.liveTimer));
    live.addEventListener('error', failed);
    void live.play().catch(failed);
    this.liveTimer = window.setTimeout(failed, NO_SIGNAL_MS);
    this.show({ badge: 'FM', name: channel.station.name, detail: `Live · ${channel.station.detail}` });
  }

  private noSignal(): void {
    if (this.channels[this.index]?.kind !== 'live') return;
    this.show({ badge: 'FM', name: 'No signal', detail: 'Trying the next station…' });
    window.setTimeout(() => {
      if (!this.paused) void this.move(1);
    }, 800);
  }

  private stopAll(): void {
    this.sequencer?.stop();
    this.sequencer = null;
    window.clearTimeout(this.liveTimer);
    if (this.live) {
      const old = this.live;
      this.live = null;
      old.pause();
      old.src = '';
    }
  }

  private ensureSpeaker(ctx: AudioContext): GainNode {
    this.speaker ??= createSpeaker(ctx);
    return this.speaker;
  }

  private idOf(channel: Channel): string {
    return channel.kind === 'off' ? 'off' : `${channel.kind}:${channel.station.id}`;
  }

  private show(now: Omit<NowPlaying, 'paused'>): void {
    this.now = { badge: now.badge, name: now.name, detail: now.detail, paused: this.paused };
    this.onChange(this.now);
  }
}
